import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { decodeToken, getToken } from '../../utils/TokenUtils';
import axios from 'axios';
import { API_URL, ENDPOINTS } from '../../constants/api';

const SUBMIT_URL = `${API_URL}/${ENDPOINTS.SYS}/create/register-caafi_delivery_note`;

export default function MaternityDeliveryNote() {
  const token = getToken();
  const user_id = decodeToken(token, 'id');
  const { id } = useParams(); // appointment_id from route

  const [data, setData] = useState(
    Object.fromEntries(
      DELIVERY_NOTE_FIELDS.map(f => [f.key, f.default || ""])
    )
  );

  const [saved, setSaved] = useState(false); // track submission success

  // Set appointment_id and user_id on load
  useEffect(() => {
    setData(prev => ({ ...prev, appointment_id: id, user_id }));
  }, [id, user_id]);

  const handle = (key) => (e) => setData({ ...data, [key]: e.target.value });

  const payload = useMemo(() => ({
    ...data,
    birth_datetime: new Date(data.birth_datetime).toISOString(),
  }), [data]);

  const handleSubmit = (e) => {
    e.preventDefault();

    axios
      .post(SUBMIT_URL, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      })
      .then((response) => {
        console.log("Submitted:", payload, "Response:", response.data);
        setSaved(true); // mark as saved
      })
      .catch((error) => {
        console.error("Error submitting:", payload, error);
        alert("Failed to save delivery note."); // fallback for error
      });
  };

  return (
    <div style={{ maxWidth: 1200, margin: "20px auto", padding: 20, border: "1px solid #ddd", borderRadius: 8 }}>
      <h1 style={{ textAlign: "center", fontSize: 24 }}>Maternity Delivery Note</h1>

      <form onSubmit={handleSubmit}>
        {/* Pre-Delivery Fields */}
        <div style={sectionStyle}>
          <h2 style={h2Style}>Pre-Delivery</h2>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {["lmp", "edd", "gestational_age_text", "gp", "bp", "pr", "fhr", "dilatation", "effacement", "membrane", "engagement"].map(key => (
              <div key={key} style={{ flex: "1 1 200px" }}>
                <label>{key.toUpperCase()}</label>
                <input type="text" style={inputStyle} value={data[key]} onChange={handle(key)} />
              </div>
            ))}
          </div>

          <label>Long Notes</label>
          <textarea style={textareaStyle} value={data.long_notes} onChange={handle("long_notes")} />
        </div>

        {/* Birth Details */}
        <div style={sectionStyle}>
          <h2 style={h2Style}>Post-Delivery</h2>

          <div>
            <label>Birth Date & Time</label>
            <input type="datetime-local" style={inputStyle} value={data.birth_datetime} onChange={handle("birth_datetime")} />
          </div>

          <label>Sex</label>
          <input type="text" style={inputStyle} value={data.sex} onChange={handle("sex")} />

          <label>Mode of Delivery</label>
          <select style={inputStyle} value={data.mode_of_delivery} onChange={(e) => setData({ ...data, mode_of_delivery: e.target.value })}>
            <option value="normal">Normal</option>
            <option value="assisted">Assisted</option>
            <option value="c_section">C-Section</option>
          </select>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div>
              <label>Gestational Age – Weeks</label>
              <input type="text" style={inputStyle} value={data.ga_weeks} onChange={handle("ga_weeks")} />
            </div>
            <div>
              <label>Gestational Age – Days</label>
              <input type="text" style={inputStyle} value={data.ga_days} onChange={handle("ga_days")} />
            </div>
            <div>
              <label>Weight</label>
              <input type="text" style={inputStyle} value={data.weight} onChange={handle("weight")} />
            </div>
            <div>
              <label>Length</label>
              <input type="text" style={inputStyle} value={data.length} onChange={handle("length")} />
            </div>
          </div>

          <div>
            {["arrival_midwife", "arrival_midwife_time", "assistant", "assistant_time"].map(key => (
              <div key={key} style={{ marginBottom: 12 }}>
                <label style={{ display: "block", marginBottom: 4 }}>{key.replace("_", " ").toUpperCase()}</label>
                <input type="text" style={{ ...inputStyle, width: "100%" }} value={data[key]} onChange={handle(key)} />
              </div>
            ))}
          </div>
        </div>

        {/* Submit Button or Success Button */}
        <div style={{ marginTop: 20 }}>
          {saved ? (
            <button style={{ ...buttonStyle, backgroundColor: "green", color: "white", cursor: "default" }} disabled>
              Saved successfully!
            </button>
          ) : (
            <button type="submit" style={{ ...buttonStyle, backgroundColor: "#4f46e5", color: "white" }}>
              Save
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

// Styles
const sectionStyle = { marginBottom: 20, padding: 10, border: "1px solid #ccc", borderRadius: 6, backgroundColor: "#f9f9f9" };
const h2Style = { fontSize: 18, marginBottom: 10 };
const inputStyle = { width: "100%", padding: 6, marginBottom: 10, borderRadius: 4, border: "1px solid #ccc" };
const textareaStyle = { width: "100%", padding: 6, minHeight: 60, marginBottom: 10, borderRadius: 4, border: "1px solid #ccc" };
const buttonStyle = { padding: "6px 12px", borderRadius: 4, border: "none" };

// Clean Field defaults
export const DELIVERY_NOTE_FIELDS = [
  { key: "appointment_id", default: "" },
  { key: "user_id", default: "" },
  { key: "lmp", default: "" },
  { key: "edd", default: "" },
  { key: "gestational_age_text", default: "" },
  { key: "gp", default: "" },
  { key: "bp", default: "" },
  { key: "pr", default: "" },
  { key: "fhr", default: "" },
  { key: "dilatation", default: "" },
  { key: "effacement", default: "" },
  { key: "membrane", default: "" },
  { key: "engagement", default: "" },
  { key: "long_notes", default: "" },
  { key: "birth_datetime", default: new Date().toISOString().slice(0, 16) },
  { key: "sex", default: "" },
  { key: "mode_of_delivery", default: "normal" },
  { key: "ga_weeks", default: "" },
  { key: "ga_days", default: "" },
  { key: "weight", default: "" },
  { key: "length", default: "" },
  { key: "arrival_midwife", default: "" },
  { key: "arrival_midwife_time", default: "" },
  { key: "assistant", default: "" },
  { key: "assistant_time", default: "" },
];
